import { Link, useLocation } from "react-router-dom";
import {
  MdContentPaste,
  MdOutlineStoreMallDirectory,
  MdOutlineLogout,
  MdOutlineGroups,
  MdContentCopy,
  MdFace,
  MdOutlineBuild,
  MdOutlineWorkOutline,
  MdOutlineMonitorHeart,
  MdMenu,
  MdDashboard,
  MdLocationOn,
  MdPerson,
  MdSettings,
  MdExpandMore,
  MdExpandLess,
  MdChevronLeft,
  MdChevronRight,
  MdMap,
  MdNotifications,
  MdClose,
  MdAssessment,
  MdHome,
  MdSecurity,
  MdVerifiedUser,
} from "react-icons/md";
import { FiUser, FiBell } from "react-icons/fi";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { PageTransition } from "./PageTransition";
import { CanAccess } from "./CanAccess";
import logoImg from "../assets/logo.png";

interface LayoutProps {
  children: JSX.Element;
}

function MenuLink({
  to,
  icon: Icon,
  children,
  className = "",
  isCollapsed = false,
  onClick,
  isActive = false,
}: any) {
  return (
    <Link
      to={to}
      className={`flex items-center w-full min-h-[52px] text-sm text-white rounded-xl transition-all duration-200 ${
        isActive
          ? "bg-white/20 shadow-lg"
          : "hover:bg-white/10"
      } ${className}`}
      title={isCollapsed ? children : undefined}
      onClick={onClick}
    >
      <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${isActive ? "bg-white/20" : ""} ${isCollapsed ? "mx-auto" : "ml-2"}`}>
        <Icon size={22} />
      </div>
      {!isCollapsed && <span className="ml-3 font-medium">{children}</span>}
    </Link>
  );
}

function SubMenuLink({
  to,
  icon: Icon,
  children,
  isCollapsed = false,
  onClick,
  isActive = false,
}: any) {
  return (
    <Link
      to={to}
      className={`flex items-center w-full min-h-[44px] text-sm text-white/90 rounded-lg transition-all duration-200 ${
        isActive
          ? "bg-white/15"
          : "hover:bg-white/10"
      } ${isCollapsed ? "justify-center" : "pl-4"}`}
      title={isCollapsed ? children : undefined}
      onClick={onClick}
    >
      <Icon size={18} className={isCollapsed ? "" : "mr-3"} />
      {!isCollapsed && <span>{children}</span>}
    </Link>
  );
}

export function Layout({ children }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  // Fecha o menu no mobile quando clicar em um link
  const closeMenuOnMobile = () => {
    if (window.innerWidth < 768) {
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <header className="h-[80px] w-full flex-shrink-0 flex items-center justify-between bg-gradient-to-r from-[#7AB550] to-[#92C163] px-6 shadow-md">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 p-2 rounded-xl backdrop-blur-sm">
            <img
              src={logoImg}
              alt="Luvilimp"
              className="h-10 w-auto object-contain"
            />
          </div>
          <div className="hidden sm:flex flex-col">
            <span className="text-sm text-white/80">Bem-vindo,</span>
            <span className="text-lg font-semibold text-white">
              {user?.name || "Usuário"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/profile"
            className="group flex items-center gap-3 px-4 py-2 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm transition-all duration-200"
            title="Meu Perfil"
          >
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden ring-2 ring-white/30 group-hover:ring-white/50 transition-all">
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <FiUser size={20} className="text-white" />
              )}
            </div>
            <span className="hidden md:block text-sm font-medium text-white">
              Meu Perfil
            </span>
          </Link>
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white transition-all"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <MdClose size={24} /> : <MdMenu size={24} />}
          </button>
        </div>
      </header>
      <div className="flex-grow flex flex-col md:flex-row overflow-hidden">
        {/* Overlay para mobile */}
        <div
          className={`fixed inset-0 bg-black/50 z-[9998] md:hidden backdrop-blur-sm transition-opacity duration-300 ${
            isMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setIsMenuOpen(false)}
        />
        <nav
          className={`bg-gradient-to-b from-[#0085BD] to-[#006a96] w-full md:flex-shrink-0 transition-all duration-300 ease-in-out ${
            isMenuCollapsed
              ? "md:w-20 md:min-w-[80px]"
              : "md:w-64 md:min-w-[256px]"
          } fixed md:relative inset-y-0 left-0 w-[85%] max-w-[300px] top-0 z-[9999] overflow-y-auto shadow-2xl transform md:transform-none md:shadow-none ${
            isMenuOpen
              ? "translate-x-0"
              : "-translate-x-full md:translate-x-0"
          }`}
        >
          {/* Header do menu mobile */}
          <div className="md:hidden flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-xl">
                <img src={logoImg} alt="Luvilimp" className="h-8 w-auto" />
              </div>
              <div>
                <p className="text-white/70 text-xs">Bem-vindo,</p>
                <p className="text-white font-semibold text-sm">{user?.name || "Usuário"}</p>
              </div>
            </div>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white"
            >
              <MdClose size={22} />
            </button>
          </div>

          <div className={`p-4 ${isMenuCollapsed ? "px-2" : ""}`}>
            <div className="hidden md:flex items-center justify-between mb-4">
              {!isMenuCollapsed && (
                <h2 className="text-lg font-semibold text-white/90">Menu</h2>
              )}
              <button
                type="button"
                onClick={() => setIsMenuCollapsed(!isMenuCollapsed)}
                className={`text-white hover:bg-white/10 p-2 rounded-lg transition-colors ${
                  isMenuCollapsed ? "mx-auto" : ""
                }`}
                title={isMenuCollapsed ? "Expandir menu" : "Minimizar menu"}
              >
                {isMenuCollapsed ? (
                  <MdChevronRight size={24} />
                ) : (
                  <MdChevronLeft size={24} />
                )}
              </button>
            </div>

            <div className="space-y-1">
              <MenuLink
                to="/home"
                icon={MdHome}
                isCollapsed={isMenuCollapsed}
                onClick={closeMenuOnMobile}
                isActive={location.pathname === "/home"}
              >
                Início
              </MenuLink>
              <CanAccess permission="dashboard:view" allowAdmin>
                <MenuLink
                  to="/dashboard"
                  icon={MdDashboard}
                  isCollapsed={isMenuCollapsed}
                  onClick={closeMenuOnMobile}
                  isActive={location.pathname === "/dashboard"}
                >
                  Dashboard
                </MenuLink>
              </CanAccess>
              <CanAccess permission="tech-visit-reports:read" allowAdmin>
                <MenuLink
                  to="/tech-visit-reports"
                  icon={MdContentPaste}
                  isCollapsed={isMenuCollapsed}
                  onClick={closeMenuOnMobile}
                  isActive={location.pathname.startsWith("/tech-visit-reports")}
                >
                  Relatórios
                </MenuLink>
              </CanAccess>
              <CanAccess permission="managerial-report:view" allowAdmin>
                <MenuLink
                  to="/managerial-report"
                  icon={MdAssessment}
                  isCollapsed={isMenuCollapsed}
                  onClick={closeMenuOnMobile}
                  isActive={location.pathname === "/managerial-report"}
                >
                  Rel. Gerencial
                </MenuLink>
              </CanAccess>
              <CanAccess permission="costumers-monitoring:view" allowAdmin>
                <MenuLink
                  to="/costumers/monitoring"
                  icon={MdOutlineMonitorHeart}
                  isCollapsed={isMenuCollapsed}
                  onClick={closeMenuOnMobile}
                  isActive={location.pathname === "/costumers/monitoring"}
                >
                  Monitoramento
                </MenuLink>
              </CanAccess>
              <CanAccess permission="costumers-map:view" allowAdmin>
                <MenuLink
                  to="/costumers/map"
                  icon={MdMap}
                  isCollapsed={isMenuCollapsed}
                  onClick={closeMenuOnMobile}
                  isActive={location.pathname === "/costumers/map"}
                >
                  Mapa
                </MenuLink>
              </CanAccess>
              <CanAccess permission="costumers:read" allowAdmin>
                <MenuLink
                  to="/costumers"
                  icon={MdOutlineStoreMallDirectory}
                  isCollapsed={isMenuCollapsed}
                  onClick={closeMenuOnMobile}
                  isActive={location.pathname === "/costumers" || location.pathname.startsWith("/costumers/") && !location.pathname.includes("monitoring") && !location.pathname.includes("map")}
                >
                  Clientes
                </MenuLink>
              </CanAccess>
              <CanAccess permission="users:read" allowAdmin>
                <MenuLink
                  to="/users"
                  icon={MdOutlineGroups}
                  isCollapsed={isMenuCollapsed}
                  onClick={closeMenuOnMobile}
                  isActive={location.pathname.startsWith("/users")}
                >
                  Usuários
                </MenuLink>
              </CanAccess>

              {/* Divisor */}
              <div className="my-3 border-t border-white/10" />

              {/* Submenu Configurações */}
              <div>
                <button
                  type="button"
                  onClick={() => setIsConfigOpen(!isConfigOpen)}
                  className={`flex items-center w-full min-h-[52px] text-sm text-white rounded-xl transition-all duration-200 ${
                    isConfigOpen ? "bg-white/10" : "hover:bg-white/10"
                  } ${isMenuCollapsed ? "justify-center" : "justify-between"}`}
                  title={isMenuCollapsed ? "Configurações" : undefined}
                >
                  {isMenuCollapsed ? (
                    <div className="w-10 h-10 flex items-center justify-center">
                      <MdSettings size={22} />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center">
                        <div className="w-10 h-10 flex items-center justify-center ml-2">
                          <MdSettings size={22} />
                        </div>
                        <span className="ml-3 font-medium">Configurações</span>
                      </div>
                      <MdExpandMore
                        size={20}
                        className={`mr-3 transition-transform duration-300 ${isConfigOpen ? "rotate-180" : ""}`}
                      />
                    </>
                  )}
                </button>
                {!isMenuCollapsed && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isConfigOpen
                        ? "max-h-[300px] opacity-100"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <div className="mt-1 ml-4 space-y-1 border-l-2 border-white/20 pl-2">
                      <CanAccess permission="equipments:read" allowAdmin>
                        <SubMenuLink
                          to="/equipments"
                          icon={MdOutlineBuild}
                          isCollapsed={isMenuCollapsed}
                          onClick={closeMenuOnMobile}
                          isActive={location.pathname.startsWith("/equipments")}
                        >
                          Equipamentos
                        </SubMenuLink>
                      </CanAccess>
                      <CanAccess permission="services:read" allowAdmin>
                        <SubMenuLink
                          to="/services"
                          icon={MdOutlineWorkOutline}
                          isCollapsed={isMenuCollapsed}
                          onClick={closeMenuOnMobile}
                          isActive={location.pathname.startsWith("/services")}
                        >
                          Serviços
                        </SubMenuLink>
                      </CanAccess>
                      <CanAccess permission="sectors:read" allowAdmin>
                        <SubMenuLink
                          to="/sectors"
                          icon={MdContentCopy}
                          isCollapsed={isMenuCollapsed}
                          onClick={closeMenuOnMobile}
                          isActive={location.pathname.startsWith("/sectors")}
                        >
                          Setores
                        </SubMenuLink>
                      </CanAccess>
                      <CanAccess permission="zones:read" allowAdmin>
                        <SubMenuLink
                          to="/zones"
                          icon={MdLocationOn}
                          isCollapsed={isMenuCollapsed}
                          onClick={closeMenuOnMobile}
                          isActive={location.pathname.startsWith("/zones")}
                        >
                          Zonas
                        </SubMenuLink>
                      </CanAccess>
                      <CanAccess permission="responsibles:read" allowAdmin>
                        <SubMenuLink
                          to="/responsibles"
                          icon={MdFace}
                          isCollapsed={isMenuCollapsed}
                          onClick={closeMenuOnMobile}
                          isActive={location.pathname.startsWith("/responsibles")}
                        >
                          Responsáveis
                        </SubMenuLink>
                      </CanAccess>
                      <CanAccess permission="profiles:read" allowAdmin>
                        <SubMenuLink
                          to="/profiles"
                          icon={MdVerifiedUser}
                          isCollapsed={isMenuCollapsed}
                          onClick={closeMenuOnMobile}
                          isActive={location.pathname.startsWith("/profiles")}
                        >
                          Perfis de Acesso
                        </SubMenuLink>
                      </CanAccess>
                      <CanAccess permission="permissions:read" allowAdmin>
                        <SubMenuLink
                          to="/permissions"
                          icon={MdSecurity}
                          isCollapsed={isMenuCollapsed}
                          onClick={closeMenuOnMobile}
                          isActive={location.pathname.startsWith("/permissions")}
                        >
                          Permissões
                        </SubMenuLink>
                      </CanAccess>
                    </div>
                  </div>
                )}
              </div>

              {/* Divisor */}
              <div className="my-3 border-t border-white/10" />

              <MenuLink
                to="/profile"
                icon={MdPerson}
                isCollapsed={isMenuCollapsed}
                onClick={closeMenuOnMobile}
                isActive={location.pathname === "/profile"}
              >
                Meu Perfil
              </MenuLink>
              <MenuLink
                to="/signin"
                icon={MdOutlineLogout}
                className="bg-red-500/80 hover:bg-red-600"
                isCollapsed={isMenuCollapsed}
                onClick={closeMenuOnMobile}
              >
                Sair
              </MenuLink>
            </div>
          </div>
        </nav>
        <main className="flex-grow relative overflow-y-auto overflow-x-hidden min-w-0 bg-gradient-to-br from-[#E5F3F7] via-[#f0f7fa] to-[#e8f4f8]">
          {/* Background decorativo */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#0085BD]/5 rounded-full blur-3xl" />
            <div className="absolute top-1/2 -left-24 w-72 h-72 bg-[#92C163]/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 right-1/4 w-80 h-80 bg-[#0085BD]/3 rounded-full blur-3xl" />
          </div>

          {/* Padrão sutil de grid */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.03]"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, #0085BD 1px, transparent 0)`,
              backgroundSize: '24px 24px'
            }}
          />

          {/* Conteúdo */}
          <div className="relative p-6">
            <PageTransition>{children}</PageTransition>
          </div>
        </main>
      </div>
    </div>
  );
}
